import React from 'react';
import { ArrowRight, Users, Target, Zap, TrendingUp, CheckCircle2, Briefcase, UserCheck, Code, HeadphonesIcon, Linkedin, Mail, Instagram, BookOpen, Award, Globe, Rocket, BarChart3, Star } from 'lucide-react';

const Landing = () => {
  return (
    <div className="landing-page">
      {/* Floating Geometric Shapes */}
      <div className="floating-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              HETU – Your Growth Partner for High-Quality B2B Connections
            </h1>
            <p className="hero-subtitle">
              We connect you with real decision-makers, warm opportunities, and deals that actually convert — not random leads.
            </p>
            <div className="hero-cta-group">
              <a href="https://tidycal.com/jayp/strategy-call" target="_blank" rel="noopener noreferrer" className="btn-primary">
                Book a Call with Us
                <ArrowRight className="btn-icon" size={20} />
              </a>
              <a href="#how-it-works" className="btn-secondary">
                See How It Works
              </a>
            </div>
            <p className="hero-credibility">
              Built by a marketer with 20+ years of experience in lead generation, growth systems, and B2B acquisition.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon-wrapper">
                <Award className="stat-icon" size={32} />
              </div>
              <div className="stat-number" data-target="20">20+</div>
              <div className="stat-label">Years of Experience</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon-wrapper">
                <Rocket className="stat-icon" size={32} />
              </div>
              <div className="stat-number" data-target="300">300+</div>
              <div className="stat-label">Businesses Helped</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon-wrapper">
                <Star className="stat-icon" size={32} />
              </div>
              <div className="stat-number" data-target="95">95%</div>
              <div className="stat-label">Client Satisfaction</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon-wrapper">
                <BarChart3 className="stat-icon" size={32} />
              </div>
              <div className="stat-number" data-target="100">B2B Focus</div>
              <div className="stat-label">Quality Over Volume</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section" id="about">
        <div className="container">
          <h2 className="section-title">Meet the Mind Behind HETU</h2>
          <div className="about-content">
            <div className="about-layout">
              <div className="about-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_46124d5f-4fa6-4c05-a888-932771ec622a/artifacts/8qb1qxt1_jay%20professional%202.png" 
                  alt="Jay Patel - Founder of HETU"
                  className="about-image"
                />
              </div>
              <div className="about-text">
                <p className="about-intro">
                  <strong>Jay Patel</strong> is an industry-leading B2B growth consultant, author, and lead generation specialist with over 20 years of global experience across India, the USA, and the Middle East.
                </p>
                <p className="about-authority">
                  As a recognized thought leader in B2B acquisition, Jay has transformed the way founders and businesses approach lead generation — moving from random tactics to predictable, scalable systems that actually deliver results.
                </p>
                <div className="about-achievements">
                  <div className="achievement-item">
                    <CheckCircle2 className="achievement-icon" size={24} />
                    <span>Built and scaled multiple lead generation systems for global clients</span>
                  </div>
                  <div className="achievement-item">
                    <CheckCircle2 className="achievement-icon" size={24} />
                    <span>Worked with SaaS, recruitment, healthcare, and service businesses worldwide</span>
                  </div>
                  <div className="achievement-item">
                    <CheckCircle2 className="achievement-icon" size={24} />
                    <span>Helped companies generate consistent inbound and outbound opportunities</span>
                  </div>
                  <div className="achievement-item">
                    <CheckCircle2 className="achievement-icon" size={24} />
                    <span>Published author of "Start With Jay" — a guide to building sustainable B2B growth</span>
                  </div>
                  <div className="achievement-item">
                    <CheckCircle2 className="achievement-icon" size={24} />
                    <span>Built automation systems using cold email, LinkedIn, funnels, and AI</span>
                  </div>
                  <div className="achievement-item">
                    <CheckCircle2 className="achievement-icon" size={24} />
                    <span>Helped founders go from inconsistent leads to predictable deal flow</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Book Section */}
      <section className="book-section">
        <div className="container">
          <div className="book-content-layout">
            <div className="book-image-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_46124d5f-4fa6-4c05-a888-932771ec622a/artifacts/ut97nzsu_StartWithJay-ebook-cover-2024-15-03-12-46-6.webp" 
                alt="Start With Jay - Book Cover"
                className="book-cover-image"
              />
            </div>
            <div className="book-text-content">
              <div className="book-badge">
                <BookOpen size={20} />
                <span>Published Author</span>
              </div>
              <h2 className="book-title">Start With Jay</h2>
              <p className="book-subtitle">
                The Definitive Guide to Building Sustainable B2B Growth Systems
              </p>
              <p className="book-description">
                In this comprehensive guide, Jay Patel shares the exact frameworks, strategies, and systems he's used to help hundreds of B2B companies transform their lead generation from chaotic guesswork into predictable revenue engines.
              </p>
              <div className="book-highlights">
                <div className="book-highlight-item">
                  <CheckCircle2 className="highlight-icon" size={20} />
                  <span>Proven B2B lead generation frameworks</span>
                </div>
                <div className="book-highlight-item">
                  <CheckCircle2 className="highlight-icon" size={20} />
                  <span>Real-world case studies and examples</span>
                </div>
                <div className="book-highlight-item">
                  <CheckCircle2 className="highlight-icon" size={20} />
                  <span>Actionable strategies you can implement immediately</span>
                </div>
                <div className="book-highlight-item">
                  <CheckCircle2 className="highlight-icon" size={20} />
                  <span>Automation and AI integration tactics</span>
                </div>
              </div>
              <a 
                href="https://cosmofeed.com/vp/655f0e35855cff001d2e7d35" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-primary btn-book"
              >
                Get the Book Now
                <ArrowRight className="btn-icon" size={20} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What HETU Does */}
      <section className="services-section" id="how-it-works">
        <div className="container">
          <h2 className="section-title">What HETU Does</h2>
          <div className="services-grid">
            <div className="feature-card">
              <div className="feature-icon-enhanced">
                <Target size={32} />
              </div>
              <h3 className="feature-title">B2B Lead Generation</h3>
              <p className="feature-description">
                Targeted outbound and inbound systems designed to reach real decision-makers — not lists, not noise.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-enhanced">
                <TrendingUp size={32} />
              </div>
              <h3 className="feature-title">Warm Intros & Deal Flow</h3>
              <p className="feature-description">
                We connect you with companies already looking for services like yours, framed as introductions, not cold pitches.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-enhanced">
                <Zap size={32} />
              </div>
              <h3 className="feature-title">Outbound Outreach</h3>
              <p className="feature-description">
                Cold email and LinkedIn outreach built around relevance, timing, and clear reasons to reply.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-enhanced">
                <Users size={32} />
              </div>
              <h3 className="feature-title">Follow-Ups That Work</h3>
              <p className="feature-description">
                Thoughtful, low-pressure follow-ups that keep conversations moving without annoying prospects.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-enhanced">
                <CheckCircle2 size={32} />
              </div>
              <h3 className="feature-title">Automation & Systems</h3>
              <p className="feature-description">
                Smart automation that runs quietly in the background so lead flow stays consistent without manual work.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-enhanced">
                <Target size={32} />
              </div>
              <h3 className="feature-title">Offer, Message & Targeting</h3>
              <p className="feature-description">
                We fix positioning, messaging, and ICP so the right leads convert faster once conversations start.
              </p>
            </div>
          </div>
          <div className="services-outcome">
            <p>More conversations with the right people. Less time chasing dead ends.</p>
            <a href="https://tidycal.com/jayp/strategy-call" target="_blank" rel="noopener noreferrer" className="btn-primary">
              Book a Call with Us
              <ArrowRight className="btn-icon" size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* Process Flow Section */}
      <section className="process-section">
        <div className="container">
          <h2 className="section-title">How It Works</h2>
          <div className="process-timeline">
            <div className="process-step">
              <div className="process-number">1</div>
              <div className="process-content">
                <h3 className="process-title">Discovery Call</h3>
                <p className="process-description">We discuss your business, ideal clients, and current challenges.</p>
              </div>
            </div>
            <div className="process-connector"></div>
            <div className="process-step">
              <div className="process-number">2</div>
              <div className="process-content">
                <h3 className="process-title">Strategy & Setup</h3>
                <p className="process-description">We build your targeting, messaging, and outreach systems.</p>
              </div>
            </div>
            <div className="process-connector"></div>
            <div className="process-step">
              <div className="process-number">3</div>
              <div className="process-content">
                <h3 className="process-title">Launch & Iterate</h3>
                <p className="process-description">We launch campaigns and optimize based on real responses.</p>
              </div>
            </div>
            <div className="process-connector"></div>
            <div className="process-step">
              <div className="process-number">4</div>
              <div className="process-content">
                <h3 className="process-title">Predictable Pipeline</h3>
                <p className="process-description">You get consistent qualified conversations every week.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why HETU Works */}
      <section className="why-section">
        <div className="container">
          <h2 className="section-title">Why HETU Works</h2>
          <div className="why-visual-grid">
            <div className="why-visual-item">
              <div className="why-visual-header">
                <div className="why-icon-circle">
                  <Award size={24} />
                </div>
                <span>20+ years of hands-on marketing experience</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{width: '100%'}}></div>
              </div>
            </div>
            <div className="why-visual-item">
              <div className="why-visual-header">
                <div className="why-icon-circle">
                  <Star size={24} />
                </div>
                <span>No junior execs — strategy done personally</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{width: '100%'}}></div>
              </div>
            </div>
            <div className="why-visual-item">
              <div className="why-visual-header">
                <div className="why-icon-circle">
                  <Target size={24} />
                </div>
                <span>Focused on quality, not volume</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{width: '95%'}}></div>
              </div>
            </div>
            <div className="why-visual-item">
              <div className="why-visual-header">
                <div className="why-icon-circle">
                  <Briefcase size={24} />
                </div>
                <span>Built for B2B, SaaS, recruitment & service businesses</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{width: '98%'}}></div>
              </div>
            </div>
            <div className="why-visual-item">
              <div className="why-visual-header">
                <div className="why-icon-circle">
                  <CheckCircle2 size={24} />
                </div>
                <span>Proven systems, not experiments</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{width: '100%'}}></div>
              </div>
            </div>
            <div className="why-visual-item">
              <div className="why-visual-header">
                <div className="why-icon-circle">
                  <TrendingUp size={24} />
                </div>
                <span>Clear ROI mindset</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{width: '100%'}}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="target-section">
        <div className="container">
          <h2 className="section-title">Who This Is For</h2>
          
          <div className="target-detailed-grid">
            {/* B2B Companies */}
            <div className="target-detailed-card">
              <div className="target-card-header">
                <Briefcase className="target-icon" size={28} />
                <h3 className="target-card-title">B2B Companies (10–200 employees)</h3>
              </div>
              <ul className="target-list">
                <li>Founder-led or sales-led teams</li>
                <li>Selling high-ticket B2B services or software</li>
                <li>Looking for more qualified conversations, not more tools</li>
              </ul>
            </div>

            {/* B2B SaaS */}
            <div className="target-detailed-card">
              <div className="target-card-header">
                <Code className="target-icon" size={28} />
                <h3 className="target-card-title">B2B SaaS Companies (Seed to Series B)</h3>
              </div>
              <ul className="target-list">
                <li>Founders still close to revenue</li>
                <li>Teams hiring SDRs, AEs, or sales leaders</li>
                <li>Growth has slowed or pipeline is inconsistent</li>
              </ul>
            </div>

            {/* Recruitment & Staffing */}
            <div className="target-detailed-card">
              <div className="target-card-header">
                <UserCheck className="target-icon" size={28} />
                <h3 className="target-card-title">Recruitment & Staffing Firms</h3>
              </div>
              <ul className="target-list">
                <li>Tech, healthcare, or finance recruiters</li>
                <li>Agencies relying on referrals and relationships</li>
                <li>Firms that want warm introductions to hiring companies</li>
              </ul>
            </div>

            {/* Biotech & Life Sciences */}
            <div className="target-detailed-card">
              <div className="target-card-header">
                <Target className="target-icon" size={28} />
                <h3 className="target-card-title">Biotech & Life Sciences Firms (B2B)</h3>
              </div>
              <ul className="target-list">
                <li>CROs, CDMOs, lab services, and biotech vendors</li>
                <li>Companies selling into pharma, hospitals, or research orgs</li>
                <li>Teams looking for strategic partner or buyer introductions</li>
              </ul>
            </div>

            {/* Financial Planners */}
            <div className="target-detailed-card">
              <div className="target-card-header">
                <TrendingUp className="target-icon" size={28} />
                <h3 className="target-card-title">Financial Planners & Wealth Managers</h3>
              </div>
              <ul className="target-list">
                <li>Independent advisors and RIAs</li>
                <li>Firms focused on trust-based growth</li>
                <li>Professionals looking for introductions to business owners or high-income clients</li>
              </ul>
            </div>

            {/* Finance & Advisory */}
            <div className="target-detailed-card">
              <div className="target-card-header">
                <HeadphonesIcon className="target-icon" size={28} />
                <h3 className="target-card-title">Finance & Advisory Businesses</h3>
              </div>
              <ul className="target-list">
                <li>Accounting, tax, CFO, and corporate finance firms</li>
                <li>Relationship-driven businesses that grow through referrals</li>
                <li>Teams that want more qualified client conversations</li>
              </ul>
            </div>
          </div>

          {/* Not a Fit Section */}
          <div className="not-fit-section">
            <h3 className="not-fit-title">Not a Fit If:</h3>
            <div className="not-fit-items">
              <div className="not-fit-item">
                <span className="not-fit-x">×</span>
                <span>B2C companies</span>
              </div>
              <div className="not-fit-item">
                <span className="not-fit-x">×</span>
                <span>Low-ticket offers</span>
              </div>
              <div className="not-fit-item">
                <span className="not-fit-x">×</span>
                <span>Anyone looking for mass outreach or spam</span>
              </div>
            </div>
          </div>

          <p className="target-transition">
            If this sounds like you, let's talk about how HETU can help you build a predictable pipeline.
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <h2 className="section-title">What Our Clients Say</h2>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-stars">
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
              </div>
              <p className="testimonial-text">
                "Working with HETU transformed our lead generation completely. We went from chasing cold leads to having qualified conversations every week. Jay's approach is practical, no-nonsense, and actually works."
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <div className="author-name">Sarah Mitchell</div>
                  <div className="author-title">VP of Sales, TechCore Solutions</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
              </div>
              <p className="testimonial-text">
                "As a recruitment firm, we were drowning in tools and strategies that didn't work. HETU helped us build a system that brings warm introductions to companies actually hiring. Our pipeline is now predictable."
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <div className="author-name">Michael Chen</div>
                  <div className="author-title">Founder, Precision Recruiters</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
              </div>
              <p className="testimonial-text">
                "Jay doesn't sell you on hype or gimmicks. He builds real systems that deliver real results. Our B2B pipeline has never been more consistent, and we finally have quality conversations instead of random inquiries."
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <div className="author-name">Jennifer Rodriguez</div>
                  <div className="author-title">CEO, DataFlow Analytics</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
              </div>
              <p className="testimonial-text">
                "We were skeptical about another 'lead gen expert,' but HETU delivered exactly what was promised. No junior staff, no experiments—just proven strategies that brought us the right connections at the right time."
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <div className="author-name">David Patel</div>
                  <div className="author-title">Managing Director, Summit Financial Advisory</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
              </div>
              <p className="testimonial-text">
                "After trying multiple agencies, HETU was refreshingly different. They focus on quality relationships, not spam. Our deal flow improved within weeks, and we're now closing better-fit clients consistently."
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <div className="author-name">Amanda Foster</div>
                  <div className="author-title">Head of Growth, CloudVenture SaaS</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
                <Star size={18} fill="currentColor" />
              </div>
              <p className="testimonial-text">
                "Jay's expertise in B2B lead generation is unmatched. He helped us refine our messaging, target the right audience, and build automation that actually works. Our sales team is busier than ever with qualified leads."
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <div className="author-name">Robert Kim</div>
                  <div className="author-title">Co-Founder, InnovateLabs</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Let's See If We're a Fit</h2>
            <p className="cta-text">
              If you're serious about getting consistent B2B leads and want a system that actually works, book a quick call with us.
            </p>
            <a href="https://tidycal.com/jayp/strategy-call" target="_blank" rel="noopener noreferrer" className="btn-primary btn-large">
              Book a Call with Us
              <ArrowRight className="btn-icon" size={24} />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <h3 className="footer-brand-name">HETU</h3>
              <p className="footer-tagline">B2B Lead Generation & Growth Partner</p>
              <p className="footer-founder">Founded by Jay Patel</p>
              <p className="footer-experience">20+ Years in Lead Generation & Marketing</p>
            </div>
            <div className="footer-links">
              <h4 className="footer-heading">Quick Links</h4>
              <ul className="footer-list">
                <li><a href="#about">About Jay</a></li>
                <li><a href="#how-it-works">How It Works</a></li>
                <li><a href="https://tidycal.com/jayp/strategy-call" target="_blank" rel="noopener noreferrer">Book a Call</a></li>
              </ul>
            </div>
            <div className="footer-connect">
              <h4 className="footer-heading">Connect</h4>
              <div className="footer-social">
                <a href="https://www.linkedin.com/in/jaxpatel/" target="_blank" rel="noopener noreferrer" className="footer-social-link" aria-label="LinkedIn">
                  <Linkedin size={20} />
                </a>
                <a href="mailto:jay@hetu.me" className="footer-social-link" aria-label="Email">
                  <Mail size={20} />
                </a>
                <a href="https://www.instagram.com/startwith.jay/" target="_blank" rel="noopener noreferrer" className="footer-social-link" aria-label="Instagram">
                  <Instagram size={20} />
                </a>
                <a href="https://cosmofeed.com/vp/655f0e35855cff001d2e7d35" target="_blank" rel="noopener noreferrer" className="footer-social-link" aria-label="Get Book">
                  <BookOpen size={20} />
                </a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} HETU. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;